// Web Bluetooth thermal printer manager (singleton).
//
// Connects to a BLE ESC/POS thermal printer, keeps the handle for the session,
// and writes raw command bytes (from escpos.js) in MTU-sized chunks. It is
// generic: it pairs with `acceptAllDevices` and probes the GATT services for a
// writable characteristic, so it works with most no-name 58/80mm BLE printers
// without hardcoding a model.
//
// PLATFORM NOTE: Web Bluetooth exists only in Chrome/Edge on desktop and Chrome
// on Android. iOS/iPadOS Safari does NOT support it, so `isSupported()` returns
// false there and the app falls back to normal (HTML) printing - by design.
// It also reaches only BLE printers; Classic-Bluetooth-only (SPP) printers are
// invisible to the browser on every platform.

// GATT services commonly exposed by BLE thermal printers. Declared as
// optionalServices so the browser grants access to them after the user pairs.
const KNOWN_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb", // common ESC/POS printer service
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ffe0-0000-1000-8000-00805f9b34fb", // BLE serial (HM-10 style)
  "0000fff0-0000-1000-8000-00805f9b34fb",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // Microchip transparent UART
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
];

const PAPER_WIDTH_KEY = "pointbunny_bt_paper_width";
const CHUNK = 180; // bytes per BLE write - safe under the common 512/185 MTU caps
// Windows' BLE stack is known to hang device.gatt.connect() indefinitely on a
// stale/idle-disconnected device instead of erroring - never let a reconnect
// attempt block the "connecting" state forever.
const CONNECT_TIMEOUT_MS = 8000;

const withTimeout = (promise, ms, message) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

class BtPrinter {
  _device = null;
  _char = null;
  _reconnectPromise = null;

  isSupported() {
    return typeof navigator !== "undefined" && !!navigator.bluetooth;
  }

  isConnected() {
    return !!(this._device?.gatt?.connected && this._char);
  }

  get deviceName() {
    return this._device?.name || "Bluetooth printer";
  }

  // ESC/POS column width (58 or 80). The paper setting itself is owned by
  // printSettings.js; a 'full' (sheet printer) pick has no thermal meaning, so
  // it prints at 80mm here.
  paperWidth() {
    const v = Number(localStorage.getItem(PAPER_WIDTH_KEY));
    return v === 58 || v === 80 ? v : 80;
  }

  async _findWritable(server) {
    const services = await server.getPrimaryServices();
    for (const svc of services) {
      let chars;
      try {
        chars = await svc.getCharacteristics();
      } catch {
        continue;
      }
      for (const c of chars) {
        if (c.properties.write || c.properties.writeWithoutResponse) return c;
      }
    }
    return null;
  }

  async _attach(device) {
    const server = await withTimeout(
      device.gatt.connect(),
      CONNECT_TIMEOUT_MS,
      "Connection timed out - try Connect printer again."
    );
    const char = await this._findWritable(server);
    if (!char) {
      try {
        device.gatt.disconnect();
      } catch {
        /* noop */
      }
      throw new Error("No writable characteristic found on this printer.");
    }
    this._device = device;
    this._char = char;
    device.addEventListener("gattserverdisconnected", () => {
      this._char = null;
    });
  }

  // Opens the browser device chooser - MUST be called from a user gesture.
  async connect() {
    if (!this.isSupported())
      throw new Error("Web Bluetooth is not supported on this browser/device.");
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: KNOWN_SERVICES,
    });
    await this._attach(device);
    return this.deviceName;
  }

  // Silent reconnect to an already-permitted device (Chrome's getDevices()).
  // Returns true if (re)connected. Never throws. Concurrent callers (app boot,
  // Settings open, a print() that finds itself disconnected) share ONE in-flight
  // attempt - two overlapping device.gatt.connect() calls on the same device is
  // a likely trigger for the Windows hang the timeout above guards against.
  async reconnect() {
    if (!this.isSupported() || !navigator.bluetooth.getDevices) return false;
    if (this.isConnected()) return true;
    if (this._reconnectPromise) return this._reconnectPromise;
    this._reconnectPromise = (async () => {
      try {
        const devices = await navigator.bluetooth.getDevices();
        const device = devices[0];
        if (!device) return false;
        await this._attach(device);
        return true;
      } catch {
        return false;
      } finally {
        this._reconnectPromise = null;
      }
    })();
    return this._reconnectPromise;
  }

  async disconnect() {
    try {
      this._device?.gatt?.disconnect();
    } catch {
      /* noop */
    }
    this._device = null;
    this._char = null;
  }

  async print(bytes) {
    if (!this.isConnected()) {
      const ok = await this.reconnect();
      if (!ok || !this.isConnected())
        throw new Error("Bluetooth printer is not connected.");
    }
    const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const noResponse =
      this._char.properties.writeWithoutResponse &&
      typeof this._char.writeValueWithoutResponse === "function";
    for (let i = 0; i < data.length; i += CHUNK) {
      const slice = data.slice(i, i + CHUNK);
      if (noResponse) await this._char.writeValueWithoutResponse(slice);
      else await this._char.writeValue(slice);
      // brief pause so the printer's buffer keeps up with the stream
      await new Promise((r) => setTimeout(r, 20));
    }
  }
}

export default new BtPrinter();
